import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import {
  MdDashboard,
  MdSmartToy,
  MdLogout,
  MdAdminPanelSettings,
  MdAccountCircle,
  MdMenu,
  MdClose,
  MdHistory,
} from "react-icons/md";
import { logOut } from "../../features/auth/authSlice";
import { clearAccountData } from "../../features/account/accountSlice";
import useActivityStatus from "../../hooks/useActivityStatus";

const ACTIVITY_TIMEOUT_ENV = process.env.REACT_APP_ACTIVITY_TIMEOUT || 120;
const ACTIVITY_TIMEOUT = parseInt(ACTIVITY_TIMEOUT_ENV, 10);

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isActive = useActivityStatus(ACTIVITY_TIMEOUT);

  // Inactivity timeout effect
  useEffect(() => {
    if (isActive === false) {
      console.warn("⏱️ Admin panel inactivity timeout - logging out");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      dispatch(clearAccountData());
      dispatch(logOut());
      navigate("/", { replace: true });
    }
  }, [isActive, dispatch, navigate]);

  const navItems = [
    { path: "#", label: "Dashboard", icon: MdDashboard },
    { path: "/admin/ai-uses", label: "AI Uses", icon: MdSmartToy },
    { path: "/admin/login-history", label: "Login History", icon: MdHistory },
  ];

  // Check if current path matches any nav item (handle both exact and partial matches)
  const isActivePath = (itemPath) => {
    if (itemPath === "#") return false; // Dashboard with # is never active
    return (
      location.pathname === itemPath ||
      location.pathname.startsWith(itemPath + "/")
    );
  };

  const handleLogout = () => {
    // Clear Redux state
    dispatch(clearAccountData());
    dispatch(logOut());
    // Clear localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Redirect to landing page
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col lg:flex-row">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar Navigation */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 w-72 lg:w-72 bg-white shadow-xl flex-shrink-0 flex flex-col z-50 transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Sidebar Header with Gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-4 lg:px-5 lg:py-5">
          <div className="flex items-center space-x-2">
            <div className="bg-white/20 p-1.5 rounded-lg">
              <MdAdminPanelSettings className="text-xl text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Driver Sure</h2>
              <h4 className="text-xs text-blue-100">Admin</h4>
            </div>
          </div>
        </div>

        {/* User Profile Section */}
        {user && (
          <div className="px-4 py-3 lg:px-5 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-1.5 rounded-full flex-shrink-0">
                <MdAccountCircle className="text-lg text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-900 truncate">
                  {user.username || "Admin User"}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user.email || "admin@example.com"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
          <div className="mb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">
              Menu
            </p>
          </div>
          {navItems.map((item) => {
            const isActive = isActivePath(item.path);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`group relative flex items-center px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                  isActive
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                    : "hover:bg-gray-100 text-gray-700 hover:text-blue-600"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-lg mr-2 transition-all flex-shrink-0 ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-gray-600 group-hover:bg-blue-50 group-hover:text-blue-600"
                  }`}
                >
                  <Icon className="text-base" />
                </div>
                <span
                  className={`flex-1 text-xs lg:text-sm font-medium ${
                    isActive ? "text-white font-semibold" : "text-gray-700"
                  }`}
                >
                  {item.label}
                </span>
                {isActive && (
                  <div className="absolute right-2 w-1.5 h-1.5 bg-white rounded-full"></div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-3 lg:p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleLogout}
            className="group relative flex items-center w-full px-3 py-2 rounded-lg transition-all duration-200 bg-white hover:bg-red-50 border border-gray-200 hover:border-red-200 text-gray-700 hover:text-red-600 shadow-sm hover:shadow-md text-sm"
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg mr-2 transition-all bg-gray-100 group-hover:bg-red-100 text-gray-600 group-hover:text-red-600 flex-shrink-0">
              <MdLogout className="text-base" />
            </div>
            <span className="flex-1 text-xs lg:text-sm font-semibold text-left">
              Logout
            </span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <header className="bg-white border-b border-gray-200 shadow-sm px-4 lg:px-6 py-3 lg:py-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {sidebarOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
              </button>
              <div>
                <h1 className="text-lg lg:text-2xl font-bold text-gray-900">
                  {location.pathname.includes("ai-uses")
                    ? "AI Usage"
                    : location.pathname.includes("login-history")
                      ? "Login History"
                      : "Dashboard"}
                </h1>
                <p className="text-xs lg:text-sm text-gray-500 hidden sm:block mt-0.5">
                  {location.pathname.includes("ai-uses")
                    ? "Monitor API usage"
                    : location.pathname.includes("login-history")
                      ? "Track business account logins"
                      : "Welcome to admin"}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="px-2 lg:px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold">
                Admin
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 lg:p-6">
            <div className="max-w-7xl mx-auto">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
}
